import {
  ApiData,
  SwaggerJsonFile,
  Endpoint,
  ApiChangeInfo,
  ApiChangeType,
} from "models/apiDocumentation/apiDocumentationModels";

import { addDaysToDate } from "./dateHelpers";

export const transformSwaggerJsonFile = (
  apiSwaggerJsonFile: SwaggerJsonFile,
  noSqlApiSwaggerJsonFile: SwaggerJsonFile | null
): ApiData => {
  const mergedPaths = {
    ...apiSwaggerJsonFile.paths,
    ...(noSqlApiSwaggerJsonFile?.paths ?? {}),
  };

  const endpointList = Object.entries(mergedPaths).map(([url, path]) => {
    const getOperation = path?.get;

    const hasChangeInfo =
      getOperation?.["x-changeType"] && getOperation?.["x-changeDate"];
    const changeInfo: ApiChangeInfo | undefined = hasChangeInfo
      ? {
          type: getOperation?.["x-changeType"] as ApiChangeType,
          date: getOperation?.["x-changeDate"] as string,
          note: getOperation?.["x-changeNote"] as string | undefined,
        }
      : undefined;

    return {
      url,
      summary: getOperation?.summary,
      description: getOperation?.description,
      tags: getOperation?.tags,
      deprecated: getOperation?.deprecated,
      changeInfo,
    };
  });

  const activeEndpoints = endpointList.filter(
    (endpoint) => !endpoint.deprecated
  );
  activeEndpoints.sort((a, b) => (a.summary > b.summary ? 1 : -1));

  const noSqlApiPath =
    noSqlApiSwaggerJsonFile?.paths &&
    Object.values(noSqlApiSwaggerJsonFile.paths)[0];
  const noSqlApiTag = noSqlApiPath?.get?.tags?.[0]
    ? { name: noSqlApiPath.get.tags[0], description: "" }
    : null;

  const mergedTags = [
    ...(apiSwaggerJsonFile.tags ?? []),
    ...(noSqlApiTag ? [noSqlApiTag] : []),
  ];

  const activeTags = mergedTags.filter((t) =>
    activeEndpoints.some((e) => e.tags?.includes(t.name))
  );
  activeTags.sort((a, b) => (a.name > b.name ? 1 : -1));

  return { endpoints: activeEndpoints, tags: activeTags };
};

export const getEndpointDataByPath = (
  endpoints: Endpoint[],
  path: string
): Endpoint | null =>
  endpoints.find((item) => `/endpoint${item.url}` === path) ?? null;

export const getChangeDescriptorFromApiChangeType = (
  changeType: ApiChangeType
): string => {
  switch (changeType) {
    case "Create":
      return "New";
    case "Update":
      return "Updated";
  }
};

export const shouldShowApiChangeBadge = (
  changeInfo?: ApiChangeInfo
): boolean => {
  if (!changeInfo) {
    return false;
  }

  const changeDate = new Date(changeInfo.date);
  const today = new Date();
  const thirtyFiveDaysAgo = addDaysToDate(today, -35);

  return changeDate >= thirtyFiveDaysAgo;
};
